// QuoteApprovalPanel — renders Nexus's authoritative verdict for a quote
// (see shared QuoteStatusDto). Driven entirely by the status response;
// the portal computes nothing here.
//
// What it shows, by (control, approval_state):
//   sales + approved/auto_approved → the Nexus-authored approved quote
//       (read-only itemized lines + total). Nexus owns the line set; the
//       partner views, can't edit. A "Pay by credit card" CTA appears only
//       when CARD_PAYMENT_ENABLED is on — card is disabled for now, so the
//       quote simply stands and Argo's AR invoices after printing. There is
//       no "request an invoice" action (an approved quote is just a quote).
//   sales + none/pending           → "being prepared by your Argo rep."
//   portal + approved/auto_approved → the approved quote read-only as
//       confirmation (no pay button — the partner finalizes through the
//       existing "Place my order" flow on the editor).
//   portal + pending, submitted    → "your Argo team is reviewing this quote"
//       (we poll; the editor flips to payable when Nexus approves). An
//       unsubmitted draft gets nothing — there is nothing under review yet.
//   any   + declined               → declined notice.
//   portal + none                  → nothing (returns null; normal flow).
//
// The charge always equals approved_quote.totals.sell_total VERBATIM — the
// editor synced it onto the local quote before routing to checkout.
//
// When Nexus reports fulfillment_coverage.status == 'existing_agreement', an
// existing Argo Fulfillment Service Order already governs the recurring lines
// and we render its `message` beneath those lines. That is the whole behaviour:
// there is no fulfillment-signature step in the portal for it to suppress, so
// read "no second signature is requested" as a property of the design, not as
// something this block enforces.
//
// Nexus does not emit the field yet, so today this renders nothing at all —
// absent parses to status 'none' by design. See the receiver inventory in
// docs/NEXUS_SYNC_QUEUE.md.

import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ApprovedQuoteDto, QuoteStatusDto } from 'shared';

import { CARD_PAYMENT_ENABLED } from '../featureFlags.js';
import { formatCurrency, formatNumber } from '../i18n/formatters.js';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../pricingCopy.js';

// General Argo contact fallback so no customer-facing handoff strands the
// customer without a concrete way to reach us (email + phone in every message).
function SupportContactLine(): JSX.Element {
  const { t } = useTranslation(['status', 'common']);
  return (
    <p className="help" style={{ margin: '8px 0 0' }}>
      <Trans
        i18nKey="approval.supportContact"
        t={t}
        values={{ email: SUPPORT_EMAIL, phone: SUPPORT_PHONE }}
        components={[
          <a key="email" href={`mailto:${SUPPORT_EMAIL}`} />,
          <a key="phone" href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} />,
        ]}
      />
    </p>
  );
}

// Money is always the Nexus-authored figure, rendered verbatim (never
// recomputed) through the locale-aware formatter. A null value (total not
// yet finalized) renders the localized placeholder instead of a raw dash.
function money(
  value: number | null,
  currency: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return value == null ? t('approval.table.valuePending') : formatCurrency(value, currency);
}

function ApprovedQuoteTable({ approved }: { approved: ApprovedQuoteDto }): JSX.Element {
  const { t } = useTranslation(['status', 'common']);
  const currency = approved.currency || 'USD';
  return (
    <table>
      <thead>
        <tr>
          <th>{t('approval.table.header.item')}</th>
          <th style={{ textAlign: 'right' }}>{t('approval.table.header.quantity')}</th>
          <th style={{ textAlign: 'right' }}>{t('approval.table.header.perUnit')}</th>
          <th style={{ textAlign: 'right' }}>{t('approval.table.header.subtotal')}</th>
        </tr>
      </thead>
      <tbody>
        {approved.lines.map((l, idx) => (
          <Fragment key={`${l.service_name}-${idx}`}>
            <tr>
              <td>
                {/* Nexus-authored line content (service_name / description /
                    recurring_frequency) is rendered VERBATIM — never translated. */}
                {l.service_name}
                {l.recurring && (
                  <span className="help" style={{ display: 'block' }}>
                    {l.recurring_frequency
                      ? t('approval.table.recurringWithFrequency', {
                          frequency: l.recurring_frequency,
                        })
                      : t('approval.table.recurringLabel')}
                  </span>
                )}
                {l.description && (
                  <span className="help" style={{ display: 'block' }}>{l.description}</span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                {/* Nullable like the sell figures beside it — a rep-unsized line
                    reads "pending", not "0". */}
                {l.quantity == null ? t('approval.table.valuePending') : formatNumber(l.quantity)}
                {l.quantity != null && l.unit ? ` ${l.unit}` : ''}
              </td>
              <td style={{ textAlign: 'right' }}>{money(l.sell_per_unit, currency, t)}</td>
              <td style={{ textAlign: 'right' }}>{money(l.sell_total, currency, t)}</td>
            </tr>
          </Fragment>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className="total">{t('approval.table.totalLabel')}</td>
          <td className="total">{money(approved.totals.sell_total, currency, t)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

export function QuoteApprovalPanel({
  status,
  quoteNumber,
  paySupported,
  onPay,
  actionsDisabled = false,
  submitted,
}: {
  status: QuoteStatusDto;
  // The order's Nexus quote number, the same one its header shows, or null.
  quoteNumber: string | null;
  // False while impersonating — CARD payment is blocked then (ADR-008 §3).
  paySupported: boolean;
  onPay: () => void;
  // Hides the settlement buttons once an action is taken (or the quote has
  // advanced past approved-unpaid) so they can't be double-fired before a
  // status refetch.
  actionsDisabled?: boolean;
  // The customer has submitted the quote (local status past draft/priced).
  submitted: boolean;
}): JSX.Element | null {
  const { t } = useTranslation(['status', 'common']);
  const sales = status.control === 'sales';
  const approved = status.approved_quote;

  // ── Declined ───────────────────────────────────────────────────────
  if (status.approval_state === 'declined') {
    return (
      <div className="card">
        <div className="banner banner--error" role="alert">
          <strong>{t('approval.declined.title')}</strong>
          <p style={{ margin: '8px 0 0' }}>
            {t('approval.declined.body')}
          </p>
          <SupportContactLine />
        </div>
      </div>
    );
  }

  // ── Approved / auto-approved (authoritative payable quote) ──────────
  if (approved) {
    const hasTotal = approved.totals.sell_total != null;
    // Settlement (pay by card) appears only for a Nexus-owned (control='sales')
    // approved quote with a total. A control='portal' approval is finalized
    // through the editor's existing "Place my order" flow, so no buttons here.
    // Card is also gated by CARD_PAYMENT_ENABLED (off for now) and paySupported
    // (false while impersonating). When card is unavailable, the approved quote
    // simply stands — Argo's AR invoices after printing, so there is nothing to
    // "request" here.
    const cardAvailable = CARD_PAYMENT_ENABLED && paySupported;
    const showSettlement = sales && hasTotal && cardAvailable && !actionsDisabled;
    return (
      <div className="card pricing-block">
        <h2 style={{ marginTop: 0 }}>
          {t(sales ? 'approval.heading.sales' : 'approval.heading.portal')}
        </h2>
        {/* Customer-facing quote number, minted by Nexus and rendered VERBATIM.
            The order's own bound number, so it matches the header. */}
        {quoteNumber && (
          <p className="help" style={{ marginTop: 0 }}>
            {t('approval.quoteNumberLabel', { number: quoteNumber })}
          </p>
        )}
        {sales ? (
          <p className="help" style={{ marginTop: 0 }}>
            {t(
              cardAvailable
                ? 'approval.salesIntro.cardAvailable'
                : 'approval.salesIntro.cardUnavailable',
            )}
          </p>
        ) : (
          <p className="help" style={{ marginTop: 0 }}>
            {t('approval.portalApprovedIntro')}
          </p>
        )}
        <ApprovedQuoteTable approved={approved} />
        {/* Existing-fulfillment coverage note (Nexus fulfillment_coverage ==
            'existing_agreement'): the recurring:true lines above are governed
            by the partner's existing Argo Fulfillment Service Order. Rendering
            the note is the whole behaviour — there is no second fulfillment
            agreement in the portal for it to suppress. Placed right below the
            lines it explains. The portal never computes this — it shows exactly
            what Nexus reported, and Nexus does not emit the block yet, so this
            is unreachable today. */}
        {status.fulfillment_coverage.status === 'existing_agreement' &&
          status.fulfillment_coverage.message && (
            <div className="banner banner--info" role="note" style={{ marginTop: 16 }}>
              {/* Nexus-authored fulfillment coverage note — rendered VERBATIM. */}
              <p style={{ margin: 0 }}>{status.fulfillment_coverage.message}</p>
              {status.fulfillment_coverage.agreement_reference && (
                <p className="help" style={{ margin: '4px 0 0' }}>
                  {t('approval.agreementReference', {
                    reference: status.fulfillment_coverage.agreement_reference,
                  })}
                </p>
              )}
            </div>
          )}
        {showSettlement && (
          <div
            className="actions"
            style={{ marginTop: 16, display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}
          >
            <button type="button" className="primary" onClick={onPay}>
              {t('approval.payByCreditCard')}
            </button>
          </div>
        )}
        {/* Card feature on, but blocked because we're impersonating: card
            details are entered by the customer, not a rep (ADR-008 §3 + the
            PCI rule). Only relevant while CARD_PAYMENT_ENABLED. */}
        {CARD_PAYMENT_ENABLED && sales && hasTotal && !paySupported && (
          <p className="help" style={{ marginTop: 16 }}>
            {t('approval.impersonationBlockedNote')}
          </p>
        )}
        {sales && !hasTotal && (
          <p className="help" style={{ marginTop: 16 }}>
            {t('approval.pendingTotalNote')}
          </p>
        )}
      </div>
    );
  }

  // ── Sales-controlled, not yet approved ──────────────────────────────
  if (sales) {
    return (
      <div className="card">
        <div className="banner banner--info" role="note">
          <strong>{t('approval.salesPending.title')}</strong>
          <p style={{ margin: '8px 0 0' }}>
            {t('approval.salesPending.body')}
          </p>
          <SupportContactLine />
        </div>
      </div>
    );
  }

  // ── Self-service, pending human review ──────────────────────────────
  if (status.approval_state === 'pending' && submitted) {
    return (
      <div className="card">
        <div className="banner banner--info" role="status" aria-live="polite">
          <strong>{t('approval.reviewPending.title')}</strong>
          <p style={{ margin: '8px 0 0' }}>
            {t('approval.reviewPending.body')}
          </p>
          <SupportContactLine />
        </div>
      </div>
    );
  }

  // control == 'portal', approval none (or pending on an unsubmitted draft) →
  // normal flow, nothing here.
  return null;
}
